import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import { useAuth } from "@workos-inc/authkit-react";

type Settings = {
  calendar: boolean;
  internal: boolean;
  sent_emails: boolean;
  minimum_units: boolean;
  track_after_exported: boolean;
  insurance_defense_mode: boolean;
  check_for_red_words: boolean;
  block_bill: boolean;
  abbreviations_enabled: boolean;
  word_count: number;
  time_to_track_emails: number;
  time_to_track_docs: number;
  prof_type: string;
  practice_type: string;
  hardcoded_matter: string;
};

type SettingsContextType = {
  settings: Settings;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const defaultSettings: Settings = {
  calendar: false,
  internal: false,
  sent_emails: false,
  minimum_units: false,
  track_after_exported: true,
  insurance_defense_mode: false,
  check_for_red_words: false,
  block_bill: false,
  abbreviations_enabled: true,
  word_count: 30,
  time_to_track_emails: 20,
  time_to_track_docs: 30,
  prof_type: "",
  practice_type: "",
  hardcoded_matter: "",
};

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { makeApiRequest } = useApi();
  const { user, isLoading: isAuthLoading } = useAuth();
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSettings = useCallback(async () => {
    try {
      const startTime = performance.now();
      const response = await makeApiRequest({ endpoint: "/settings", method: "GET" });
      const s = response?.data?.[0];

      if (s) {
        setSettings({
          calendar: !!s.calendar,
          internal: !!s.internal,
          sent_emails: !!s.sent_emails,
          minimum_units: !!s.minimum_units,
          track_after_exported: !!s.track_after_exported,
          insurance_defense_mode: !!s.insurance_defense_mode,
          check_for_red_words: !!s.check_for_red_words,
          block_bill: !!s.block_bill,
          abbreviations_enabled: s.abbreviations_enabled ?? true,
          word_count: s.word_count ?? 30,
          time_to_track_emails: s.time_to_track_emails ?? 20,
          time_to_track_docs: s.time_to_track_docs ?? 30,
          prof_type: s.prof_type ?? "",
          practice_type: s.practice_type ?? "",
          hardcoded_matter: s.hardcoded_matter ?? "",
        });
      }

      logger.debug("Settings loaded", { durationMs: Math.round(performance.now() - startTime) });
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'refreshSettings' });
      }
    }
  }, [makeApiRequest]);

  useEffect(() => {
    const loadSettings = async () => {
      if (!isAuthLoading && user) {
        setIsLoading(true);
        try {
          await refreshSettings();
        } finally {
          setIsLoading(false);
        }
      }
    };
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthLoading, user]);

  // Refresh settings when user returns to tab (handles backend-initiated changes like hardcoded_matter expiration)
  useEffect(() => {
    if (!user) return;

    const handleFocus = () => refreshSettings();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, refreshSettings]);

  const value: SettingsContextType = {
    settings,
    isLoading,
    refreshSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}
